import type { CpotIndicator, Recommendation, RecommendationCriteria, VisibleRecommendation, WorkflowRoute, WorkflowRouteId } from "./types";
import type { WorkflowState } from "../lib/state";

export const workflowRoutes: WorkflowRoute[] = [
  {
    id: "start",
    path: "/start",
    label: "Start",
    shortLabel: "Start",
    group: "entry",
    summary: "Choose the phase and add patient details when the pathway needs them.",
  },
  {
    id: "setup",
    path: "/setup",
    label: "Patient details",
    shortLabel: "Details",
    group: "entry",
    summary: "Set renal function, age band, sedation state, pain score, and relevant risk flags.",
  },
  {
    id: "tools",
    path: "/tools",
    label: "Tools",
    shortLabel: "Tools",
    group: "tool",
    summary: "Pain score, functional assessment, and CPOT.",
  },
  {
    id: "before-surgery",
    path: "/before-surgery",
    label: "Before surgery",
    shortLabel: "Before",
    group: "workflow",
    summary: "Pre-operative analgesia and exclusions.",
  },
  {
    id: "during-surgery",
    path: "/during-surgery",
    label: "During surgery",
    shortLabel: "During",
    group: "workflow",
    summary: "Opioid-sparing intra-operative approach, adjuncts, cautions, and regional options.",
  },
  {
    id: "handover",
    path: "/handover",
    label: "Handover",
    shortLabel: "Handover",
    group: "workflow",
    summary: "End-of-case opioid loading and immediate ITU goals.",
  },
  {
    id: "itu-sedated",
    path: "/itu-sedated",
    label: "ITU while sedated",
    shortLabel: "Sedated",
    group: "workflow",
    summary: "Sedation, dexmedetomidine, pain goals, and IV breakthrough boluses.",
  },
  {
    id: "after-extubation",
    path: "/after-extubation",
    label: "After extubation",
    shortLabel: "Extubated",
    group: "workflow",
    summary: "Oral analgesia, NSAID cautions, and escalation thresholds after extubation.",
  },
  {
    id: "breakthrough-pain",
    path: "/breakthrough-pain",
    label: "Breakthrough pain",
    shortLabel: "Pain",
    group: "workflow",
    summary: "Practical steps when pain scores or function indicate challenging pain control.",
  },
  {
    id: "escalation",
    path: "/escalation",
    label: "Escalation",
    shortLabel: "Escalate",
    group: "workflow",
    summary: "PCA, ketamine, gabapentinoids, and Acute Pain Management Team escalation.",
  },
  {
    id: "pain-score",
    path: "/tools/pain-score",
    label: "Pain score tool",
    shortLabel: "NPS",
    group: "tool",
    summary: "Numeric Pain Score and Verbal Rating Scale.",
  },
  {
    id: "function",
    path: "/tools/function",
    label: "Function tool",
    shortLabel: "Function",
    group: "tool",
    summary: "Functional limitation assessment for breathing, coughing, mobility, and transfers.",
  },
  {
    id: "cpot",
    path: "/tools/cpot",
    label: "CPOT tool",
    shortLabel: "CPOT",
    group: "tool",
    summary: "Critical-Care Pain Observation Tool.",
  },
];

export const routeByPath = new Map(workflowRoutes.map((route) => [route.path, route]));
export const routeById = new Map(workflowRoutes.map((route) => [route.id, route]));

export const flagLabels: Record<string, string> = {
  chronicPain: "history of chronic pain",
  chronicGabapentinoid: "chronic gabapentinoid use",
  polyneuropathy: "central/peripheral polyneuropathy",
  opioidUse: "current weak/strong opioid use",
  pregabalinAllergy: "pregabalin allergy or intolerance",
  neuropathicPain: "neuropathic features",
  methadone: "current methadone",
  ivdu: "history of IVDU",
  mentalHealth: "anxiety, depression, PTSD, or pain catastrophising",
  oralNotTolerated: "oral route not tolerated",
  functionalLimitation: "significant functional limitation",
  bmiAbove30: "BMI above 30",
  weightAbove50: "weight above 50 kg",
};

export const recommendations: Recommendation[] = [
  {
    id: "pre-paracetamol",
    routeIds: ["before-surgery"],
    title: "Paracetamol before surgery",
    strength: "recommended",
    summary: "Give paracetamol 1 g PO two hours before surgery.",
    details: ["The anaesthetist assessing the patient before surgery prescribes pre-operative medications."],
    sourceRefs: [{ page: 3, section: "3.1 Pre-operative guideline" }],
  },
  {
    id: "pre-pregabalin-standard",
    routeIds: ["before-surgery"],
    title: "Pregabalin when indicated",
    strength: "consider",
    summary: "For indicated patients under 75 years with eGFR above 40, consider pregabalin 150 mg PO two hours before surgery.",
    details: [],
    cautions: ["Not indicated in patients older than 75 years or eGFR below 30."],
    sourceRefs: [{ page: 3, section: "3.1 Pre-operative guideline" }],
    criteria: { age: ["under75"], egfr: ["gt40"], flagsAny: ["chronicPain", "chronicGabapentinoid", "polyneuropathy"] },
  },
  {
    id: "pre-pregabalin-renal",
    routeIds: ["before-surgery"],
    title: "Pregabalin dose for eGFR 30-40",
    strength: "consider",
    summary: "For indicated patients under 75 years with eGFR 30-40, consider pregabalin 75 mg PO two hours before surgery.",
    details: [],
    cautions: ["Not indicated in patients older than 75 years or eGFR below 30."],
    sourceRefs: [{ page: 3, section: "3.1 Pre-operative guideline" }],
    criteria: { age: ["under75"], egfr: ["30to40"], flagsAny: ["chronicPain", "chronicGabapentinoid", "polyneuropathy"] },
  },
  {
    id: "pre-pregabalin-exclusion",
    routeIds: ["before-surgery"],
    title: "Pregabalin exclusion",
    strength: "caution",
    summary: "Pregabalin is not indicated for patients older than 75 years or those with eGFR below 30.",
    details: ["Select age and renal function in patient details before applying gabapentinoid advice."],
    sourceRefs: [{ page: 3, section: "3.1 Pre-operative guideline" }],
    criteria: { age: ["over75"], egfr: ["20to30", "lt20"] },
  },
  {
    id: "pre-gabapentin-alternative",
    routeIds: ["before-surgery"],
    title: "Gabapentin alternative",
    strength: "consider",
    summary: "For allergy or intolerance to pregabalin, the printed guideline lists gabapentin 300 mg PO four hours before surgery.",
    details: ["Refer these patients to the Acute Pain Management Team for follow-up."],
    sourceRefs: [{ page: 3, section: "3.1 Pre-operative guideline" }],
    criteria: { flagsAny: ["pregabalinAllergy"] },
  },
  {
    id: "intra-opioid-sparing",
    routeIds: ["during-surgery"],
    title: "Intra-operative opioid approach",
    strength: "recommended",
    summary: "Use fentanyl, alfentanil, or remifentanil intra-operatively for analgesia.",
    details: ["If remifentanil is used, the printed guideline advises limiting the dose to less than 0.15 mcg/kg/min."],
    cautions: ["Preferentially avoid remifentanil in cardiac surgery patients at risk of persistent post-operative pain."],
    sourceRefs: [{ page: 4, section: "3.2 Intra-operative guideline" }],
  },
  {
    id: "intra-ppp-risk",
    routeIds: ["during-surgery"],
    title: "Persistent post-operative pain risk",
    strength: "caution",
    summary: "Risk factors include anxiety/depression, post-traumatic stress, pain catastrophising, chronic pain, pre-surgery opioid use, younger age, female gender, and longer surgery.",
    details: ["The printed guideline states the incidence of persistent post-operative pain in cardiac surgery patients is in the range of 40%."],
    sourceRefs: [{ page: 4, section: "3.2 Intra-operative guideline" }],
    criteria: { flagsAny: ["chronicPain", "opioidUse", "mentalHealth"] },
  },
  {
    id: "intra-magnesium",
    routeIds: ["during-surgery"],
    title: "Magnesium sulphate",
    strength: "recommended",
    summary: "Use up to 5 g MgSO4 IV intra-operatively.",
    details: ["This is listed as an opioid-sparing adjunct in the intra-operative section."],
    sourceRefs: [{ page: 4, section: "3.2 Intra-operative guideline" }],
  },
  {
    id: "intra-dexmedetomidine",
    routeIds: ["during-surgery"],
    title: "Dexmedetomidine adjunct",
    strength: "consider",
    summary: "Dose 0.5 mcg/kg/hour IV infusion, with a stated range of 0.4-0.7 mcg/kg/hour.",
    details: ["Start as soon as central venous access is secured.", "Continue throughout the case until admission to ITU."],
    cautions: ["Avoid in high-degree heart block, severe bradycardia, or hypotension requiring noradrenaline above 0.1 mcg/kg/min."],
    sourceRefs: [{ page: 4, section: "3.2 Intra-operative guideline" }],
  },
  {
    id: "intra-ketamine",
    routeIds: ["during-surgery"],
    title: "Ketamine intra-operatively",
    strength: "consider",
    summary: "Consider ketamine 0.5 mg/kg IV as a single dose or two divided doses.",
    details: ["Indications listed include chronic pain, current weak/strong opioids, and current methadone use."],
    sourceRefs: [{ page: 4, section: "3.2 Intra-operative guideline" }],
    criteria: { flagsAny: ["chronicPain", "opioidUse", "methadone"] },
  },
  {
    id: "intra-regional",
    routeIds: ["during-surgery"],
    title: "Regional anaesthesia",
    strength: "consider",
    summary: "Maximum levobupivacaine/bupivacaine dose: 2 mg/kg.",
    details: [
      "Rectus sheath block: bilateral posterior rectus sheath injection, 10-20 ml local anaesthetic per side.",
      "Pecto-intercostal fascial block: single-level bilateral T4 injection, 10-20 ml local anaesthetic per side.",
    ],
    sourceRefs: [{ page: 4, section: "3.2 Intra-operative guideline" }],
  },
  {
    id: "handover-opioid-loading-gt30",
    routeIds: ["handover"],
    title: "End-of-case opioid loading: eGFR above 30",
    strength: "recommended",
    summary: "For eGFR above 30, use morphine 0.1-0.2 mg/kg IV or oxycodone 0.1-0.2 mg/kg IV.",
    details: ["A loading dose of a long-acting opioid should be administered at the end of surgery."],
    sourceRefs: [{ page: 4, section: "3.2 Intravenous opioid loading" }, { page: 5, section: "3.2 Intravenous opioid loading continued" }],
    criteria: { egfr: ["gt40", "30to40"] },
  },
  {
    id: "handover-opioid-loading-20-30",
    routeIds: ["handover"],
    title: "End-of-case opioid loading: eGFR 20-30",
    strength: "recommended",
    summary: "For eGFR 20-30, use oxycodone 0.1-0.2 mg/kg IV or fentanyl 2-3 mcg/kg IV.",
    details: [],
    sourceRefs: [{ page: 5, section: "3.2 Intravenous opioid loading continued" }],
    criteria: { egfr: ["20to30"] },
  },
  {
    id: "handover-opioid-loading-lt20",
    routeIds: ["handover"],
    title: "End-of-case opioid loading: eGFR below 20 or ESRF",
    strength: "recommended",
    summary: "For eGFR below 20 or ESRF, use fentanyl 2-3 mcg/kg IV.",
    details: [],
    sourceRefs: [{ page: 5, section: "3.2 Intravenous opioid loading continued" }],
    criteria: { egfr: ["lt20"] },
  },
  {
    id: "handover-adjusted-weight",
    routeIds: ["handover"],
    title: "Adjusted weight for opioid loading",
    strength: "consider",
    summary: "For BMI above 30, consider using adjusted weight for dosing.",
    details: ["This applies to end-of-case intravenous opioid loading."],
    sourceRefs: [{ page: 5, section: "3.2 Intravenous opioid loading continued" }],
    criteria: { flagsAny: ["bmiAbove30"] },
  },
  {
    id: "sedated-targets",
    routeIds: ["handover", "itu-sedated"],
    title: "Immediate post-operative pain and sedation targets",
    strength: "recommended",
    summary: "Post-operative sedation: propofol infusion 1 mg/kg/hour IV, aim for RASS 0 to +1, CPOT 2 or less, NPS below 4 or mild pain.",
    details: ["Document pain scores hourly."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
  },
  {
    id: "sedated-dexmedetomidine",
    routeIds: ["itu-sedated"],
    title: "Dexmedetomidine while sedated",
    strength: "recommended",
    summary: "Continue dexmedetomidine infusion at 0.1-0.4 mcg/kg/hour IV.",
    details: [
      "If the patient is not waking within one hour after stopping propofol, reduce dexmedetomidine by 50%.",
      "Stop dexmedetomidine one hour after the first oral analgesia has been given.",
      "Continue dexmedetomidine for a maximum of four hours after extubation.",
    ],
    cautions: ["Stop if RASS below -1, respiratory rate below 10, respiratory acidosis, systolic BP below 90 mmHg, or HR below 50."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
  },
  {
    id: "sedated-breakthrough-morphine",
    routeIds: ["itu-sedated", "breakthrough-pain"],
    title: "Breakthrough pain: morphine",
    strength: "recommended",
    summary: "If pain management is challenging, use morphine 2 mg IV bolus every 2-5 minutes up to 20 mg.",
    details: ["Use the same opioid as administered intra-operatively for opioid loading until pain control is achieved."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
    criteria: { opioid: ["morphine"] },
  },
  {
    id: "sedated-breakthrough-oxycodone",
    routeIds: ["itu-sedated", "breakthrough-pain"],
    title: "Breakthrough pain: oxycodone",
    strength: "recommended",
    summary: "If pain management is challenging, use oxycodone 2 mg IV bolus every 2-5 minutes up to 20 mg.",
    details: ["Use the same opioid as administered intra-operatively for opioid loading until pain control is achieved."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
    criteria: { opioid: ["oxycodone"] },
  },
  {
    id: "sedated-breakthrough-fentanyl",
    routeIds: ["itu-sedated", "breakthrough-pain"],
    title: "Breakthrough pain: fentanyl",
    strength: "recommended",
    summary: "If pain management is challenging, use fentanyl 20 mcg IV bolus every 2-5 minutes up to 200 mcg.",
    details: ["Use the same opioid as administered intra-operatively for opioid loading until pain control is achieved."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
    criteria: { opioid: ["fentanyl"] },
  },
  {
    id: "extubated-targets",
    routeIds: ["after-extubation"],
    title: "After extubation targets",
    strength: "recommended",
    summary: "Aim for RASS 0 to +2 and CPOT 2 or less, NPS below 4, or mild pain.",
    details: ["Give oral analgesia once it is safe to take sips of water."],
    sourceRefs: [{ page: 6, section: "After extubation" }],
  },
  {
    id: "oral-analgesia-gt30",
    routeIds: ["after-extubation"],
    title: "Oral analgesia: eGFR above 30",
    strength: "recommended",
    summary: "Dihydrocodeine 30-60 mg PO four times daily; IR morphine 10-20 mg PO 2-hourly PRN.",
    details: [],
    sourceRefs: [{ page: 6, section: "Oral analgesia guideline" }],
    criteria: { egfr: ["gt40", "30to40"] },
  },
  {
    id: "oral-analgesia-20-30",
    routeIds: ["after-extubation"],
    title: "Oral analgesia: eGFR 20-30",
    strength: "recommended",
    summary: "IR oxycodone 2.5 mg PO four times daily; IR oxycodone 2.5-5 mg PO 2-hourly PRN.",
    details: [],
    sourceRefs: [{ page: 6, section: "Oral analgesia guideline" }],
    criteria: { egfr: ["20to30"] },
  },
  {
    id: "oral-analgesia-lt20",
    routeIds: ["after-extubation"],
    title: "Oral analgesia: eGFR below 20 or ESRF",
    strength: "recommended",
    summary: "Buprenorphine 200 mcg SL four times daily; buprenorphine 200 mcg SL 4-hourly PRN.",
    details: [],
    sourceRefs: [{ page: 6, section: "Oral analgesia guideline" }],
    criteria: { egfr: ["lt20"] },
  },
  {
    id: "oral-paracetamol-over-50kg",
    routeIds: ["after-extubation"],
    title: "Regular oral paracetamol",
    strength: "recommended",
    summary: "For weight above 50 kg, give paracetamol 1 g PO four times daily.",
    details: [],
    sourceRefs: [{ page: 6, section: "Oral analgesia guideline" }],
    criteria: { flagsAny: ["weightAbove50"] },
  },
  {
    id: "nsaid-caution",
    routeIds: ["after-extubation"],
    title: "NSAID caution",
    strength: "caution",
    summary: "Ibuprofen 400 mg PO three times daily is listed, but ACCU consultant/senior physician review is required before considering this.",
    details: ["Limit NSAID therapy to a maximum of 72 hours."],
    cautions: ["Contraindications listed include ischaemic heart disease, congestive heart failure, cerebrovascular disease, peripheral vascular disease, renal impairment, history of GI bleeding, coagulation defects, and uncontrolled hypertension."],
    sourceRefs: [{ page: 6, section: "NSAIDs" }],
  },
  {
    id: "pca-trigger",
    routeIds: ["after-extubation", "breakthrough-pain", "escalation"],
    title: "Consider PCA for pain score",
    strength: "escalate",
    summary: "Consider PCA if NPS is above 4 or pain is severe despite regular oral analgesia and maximum top-up IV boluses.",
    details: [],
    sourceRefs: [{ page: 6, section: "PCA escalation" }],
    criteria: { painAtLeast: 5 },
  },
  {
    id: "pca-functional-trigger",
    routeIds: ["after-extubation", "breakthrough-pain", "escalation"],
    title: "Consider PCA for function or route",
    strength: "escalate",
    summary: "Consider PCA if there are significant functional limitations despite regular oral analgesia and maximum top-up IV boluses, or if the oral route is not tolerated.",
    details: [],
    sourceRefs: [{ page: 6, section: "PCA escalation" }],
    criteria: { flagsAny: ["oralNotTolerated", "functionalLimitation"] },
  },
  {
    id: "postop-ketamine",
    routeIds: ["breakthrough-pain", "escalation"],
    title: "Ketamine escalation",
    strength: "escalate",
    summary: "Discuss with the ITU consultant before starting ketamine for challenging pain with significant functional limitation.",
    details: [
      "Patients at risk include chronic pain, chronic weak/strong opioid use, history of IVDU, current methadone, and mental health history.",
      "The printed guideline lists IV ketamine 0.1-0.3 mg/kg/hour for 24-48 hours in Level 2 or 3 wards, then transition to oral ketamine with the first oral dose one hour after stopping the infusion.",
      "Oral ketamine is listed as 50 mg four times daily PO, tapering over 48-72 hours.",
      "Refer to the Acute Pain Management Team for follow-up.",
    ],
    sourceRefs: [{ page: 6, section: "Ketamine" }, { page: 7, section: "Ketamine continued" }],
    criteria: { flagsAny: ["chronicPain", "opioidUse", "methadone", "ivdu", "mentalHealth", "functionalLimitation"] },
  },
  {
    id: "postop-gabapentinoids",
    routeIds: ["breakthrough-pain", "escalation"],
    title: "Gabapentinoids for neuropathic features",
    strength: "escalate",
    summary: "Discuss with Acute Pain Management Team before starting post-operative gabapentinoids for challenging pain with neuropathic features.",
    details: [
      "For eGFR above 40, the printed guideline lists pregabalin 75 mg twice daily PO.",
      "For eGFR 30-40, the printed guideline lists pregabalin 50 mg twice daily PO.",
      "Duration of therapy is listed as 4-6 weeks.",
    ],
    cautions: ["Common side-effects include drowsiness, dizziness, confusion, and malaise. If present, omit morning dose and consider reducing evening dose."],
    sourceRefs: [{ page: 7, section: "Gabapentinoids" }],
    criteria: { flagsAny: ["neuropathicPain"] },
  },
  {
    id: "non-pharm",
    routeIds: ["after-extubation", "breakthrough-pain", "escalation"],
    title: "Non-pharmacological adjuncts",
    strength: "recommended",
    summary: "Non-pharmacological therapy, such as music and massage, is recommended as an adjunct to pharmacological therapy.",
    details: ["Use alongside pharmacological treatment rather than as a substitute when pain is clinically significant."],
    sourceRefs: [{ page: 7, section: "Non-pharmacological approaches" }],
  },
];

export const painScaleRows = [
  { range: "0", label: "No pain" },
  { range: "1-3", label: "Mild pain" },
  { range: "4-6", label: "Moderate pain" },
  { range: "7-10", label: "Severe pain" },
];

export const functionalAssessment = [
  {
    grade: "A",
    label: "No limitation",
    description: "The patient can undertake the activity without limitation due to pain, with pain intensity typically 0-3.",
  },
  {
    grade: "B",
    label: "Mild limitation",
    description: "The patient can undertake the activity but experiences moderate to severe pain, with pain intensity typically 4-10.",
  },
  {
    grade: "C",
    label: "Significant limitation",
    description: "The patient is unable to complete the activity due to pain or pain-treatment adverse effects, independent of pain intensity score.",
  },
];

export const cpotIndicators: CpotIndicator[] = [
  {
    id: "facial",
    label: "Facial expression",
    patientGroup: "all",
    options: [
      { label: "Relaxed, neutral", score: 0, description: "No muscle tension observed." },
      { label: "Tense", score: 1, description: "Frowning, brow lowering, orbit tightening, levator contraction, or eye opening/tearing during nociception." },
      { label: "Grimacing", score: 2, description: "Clear grimacing expression." },
    ],
  },
  {
    id: "movement",
    label: "Body movements",
    patientGroup: "all",
    options: [
      { label: "Absence of movements or normal position", score: 0, description: "No movement or normal movement not aimed toward the pain site." },
      { label: "Protection", score: 1, description: "Slow cautious movements, touching or rubbing pain site, or seeking attention through movements." },
      { label: "Restlessness / agitation", score: 2, description: "Pulling tube, attempting to sit up, thrashing, not following commands, striking staff, or trying to climb out of bed." },
    ],
  },
  {
    id: "ventilator",
    label: "Compliance with ventilator",
    patientGroup: "intubated",
    options: [
      { label: "Tolerating ventilator or movement", score: 0, description: "Alarms not activated, easy ventilation." },
      { label: "Coughing but tolerating", score: 1, description: "Coughing; alarms may activate but stop spontaneously." },
      { label: "Fighting ventilator", score: 2, description: "Asynchrony, blocking ventilation, alarms frequently activated." },
    ],
  },
  {
    id: "vocalisation",
    label: "Vocalisation",
    patientGroup: "extubated",
    options: [
      { label: "Normal tone or no sound", score: 0, description: "Talking in a normal tone or no sound." },
      { label: "Sighing, moaning", score: 1, description: "Audible sighing or moaning." },
      { label: "Crying out, sobbing", score: 2, description: "Crying out or sobbing." },
    ],
  },
  {
    id: "muscle",
    label: "Muscle tension",
    patientGroup: "all",
    options: [
      { label: "Relaxed", score: 0, description: "No resistance to passive movements." },
      { label: "Tense, rigid", score: 1, description: "Resistance to passive movements." },
      { label: "Very tense or rigid", score: 2, description: "Strong resistance to passive movements or incapacity to complete them." },
    ],
  },
];

function criteriaMatches(criteria: RecommendationCriteria | undefined, state: WorkflowState) {
  const missingContext: string[] = [];
  if (!criteria) {
    return { isMatch: true, missingContext };
  }

  let isMatch = true;
  if (criteria.age) {
    if (state.age === "unknown") {
      missingContext.push("age band");
    } else if (!criteria.age.includes(state.age)) {
      isMatch = false;
    }
  }

  if (criteria.egfr) {
    if (state.egfr === "unknown") {
      missingContext.push("eGFR band");
    } else if (!criteria.egfr.includes(state.egfr)) {
      isMatch = false;
    }
  }

  if (criteria.sedation) {
    if (state.sedation === "unknown") {
      missingContext.push("sedation state");
    } else if (!criteria.sedation.includes(state.sedation)) {
      isMatch = false;
    }
  }

  if (criteria.opioid) {
    if (state.opioid === "unknown") {
      missingContext.push("intra-operative opioid");
    } else if (!criteria.opioid.includes(state.opioid)) {
      isMatch = false;
    }
  }

  if (criteria.flagsAny && criteria.flagsAny.length > 0) {
    const hasFlag = criteria.flagsAny.some((flag) => state.flags.includes(flag as never));
    if (!hasFlag) {
      isMatch = false;
    }
  }

  if (criteria.painAtLeast !== undefined) {
    if (state.pain === null) {
      missingContext.push("pain score");
    } else if (state.pain < criteria.painAtLeast) {
      isMatch = false;
    }
  }

  if (criteria.cpotAtLeast !== undefined) {
    if (state.cpot === null) {
      missingContext.push("CPOT");
    } else if (state.cpot < criteria.cpotAtLeast) {
      isMatch = false;
    }
  }

  return { isMatch, missingContext };
}

export function visibleRecommendations(routeId: WorkflowRouteId, state: WorkflowState): VisibleRecommendation[] {
  return recommendations
    .filter((recommendation) => recommendation.routeIds.includes(routeId))
    .map((recommendation) => {
      const result = criteriaMatches(recommendation.criteria, state);
      return {
        ...recommendation,
        isContextualMatch: result.isMatch,
        missingContext: result.missingContext,
      };
    })
    .filter((recommendation) => recommendation.isContextualMatch || recommendation.missingContext.length > 0);
}
